/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 *
 * REGLE DE GESTION V5.4 : le scan materiel est reutilisable depuis le Parc et
 * les interventions. QR code, code-barres, numero d'inventaire et numero de
 * serie doivent aboutir au meme objet materiel.
 */
import { useEffect, useRef, useState } from 'react';
import { assetsApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Camera, MagnifyingGlass, QrCode } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AssetScannerDialog({ open, onOpenChange, assets = [], onSelect, title = 'Scanner ou rechercher un matériel' }) {
  const [manual, setManual] = useState('');
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);

  const stop = () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    streamRef.current?.getTracks?.().forEach(t => t.stop());
    streamRef.current = null;
    setCamera(false);
  };

  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const normalize = value => String(value || '').trim().toLowerCase();
  const findLocal = raw => {
    const needle = normalize(raw);
    return assets.find(a => [
      a.inventory_number, a.external_inventory_number, a.internal_inventory_number,
      a.serial_number, a.hostname, a.asset_tag, a.barcode
    ].some(v => normalize(v) === needle));
  };

  const resolve = async raw => {
    const value = String(raw || '').trim();
    if (!value) return;
    setBusy(true);
    try {
      const local = findLocal(value);
      if (local) { onSelect?.(local); onOpenChange(false); return; }
      let token = value;
      try {
        const u = new URL(value);
        token = u.searchParams.get('scan') || u.pathname.split('/').filter(Boolean).pop() || value;
      } catch (_) {}
      token = String(token).replace(/^ASSET:/i, '');
      const res = await assetsApi.getByPublicToken(token);
      if (res.data) { onSelect?.(res.data); onOpenChange(false); return; }
      throw new Error('not found');
    } catch (_) {
      toast.error('Matériel non reconnu. Essayez un code inventaire, un numéro de série, un QR ou un code-barres.');
    } finally { setBusy(false); }
  };

  const start = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) return toast.error('Caméra non disponible sur cet appareil');
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
      streamRef.current = stream;
      // REGLE DE GESTION : le <video> n'existe dans le DOM qu'apres le passage en mode camera.
      // On attend donc son montage avant d'attacher le flux et de lancer le detecteur.
      setCamera(true);
      let video = null;
      for (let i=0;i<20&&!video;i+=1) {
        await new Promise(resolve=>setTimeout(resolve,25));
        video = videoRef.current;
      }
      if (!video) throw new Error('video unavailable');
      video.srcObject = stream;
      await video.play();
      if (!('BarcodeDetector' in window)) {
        toast.info('Lecture automatique non disponible sur ce navigateur. Vous pouvez saisir le code sous la caméra.');
        return;
      }
      let formats = [];
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats?.();
        const wanted = ['qr_code','code_128','code_39','ean_13','ean_8','data_matrix','itf'];
        formats = wanted.filter(x => !supported || supported.includes(x));
      } catch (_) { formats = ['qr_code']; }
      const detector = new window.BarcodeDetector(formats.length ? { formats } : undefined);
      const loop = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes?.[0]?.rawValue) {
            const raw = codes[0].rawValue;
            stop();
            await resolve(raw);
            return;
          }
        } catch (_) {}
        frameRef.current = requestAnimationFrame(loop);
      };
      frameRef.current = requestAnimationFrame(loop);
    } catch (_) { toast.error('Impossible d’ouvrir la caméra'); }
  };

  return <Dialog open={open} onOpenChange={v => { if (!v) stop(); onOpenChange(v); }}>
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="rounded-2xl overflow-hidden bg-slate-950 min-h-48 flex items-center justify-center border">
          {camera ? <video ref={videoRef} className="w-full max-h-80 object-cover" playsInline muted/> : <div className="text-center text-white/70"><QrCode size={68} className="mx-auto"/><div className="text-xs mt-2">QR · Code-barres · DataMatrix</div></div>}
        </div>
        <Button className="w-full" variant="outline" onClick={camera ? stop : start} disabled={busy}><Camera size={17} className="mr-2"/>{camera ? 'Fermer la caméra' : 'Ouvrir la caméra'}</Button>
        <div className="text-[11px] text-slate-500 text-center">Inventaire Holding, code Planigo, numéro de série ou contenu du QR/code-barres</div>
        <div className="flex gap-2"><Input value={manual} onChange={e=>setManual(e.target.value)} onKeyDown={e=>e.key==='Enter'&&resolve(manual)} placeholder="INV-... / MAT-... / S/N ..."/><Button onClick={()=>resolve(manual)} disabled={busy}><MagnifyingGlass size={17}/></Button></div>
      </div>
    </DialogContent>
  </Dialog>;
}
